import { TIME_ZONES } from "@/domain/market/timeZones";


/**
 * UI metadata for settings fields. Validation lives server-side (zod); this only drives presentation.
 * `label`, `help`, `suffix` and option labels are i18n keys, translated at render time.
 */
export interface FieldMeta {
  label: string;
  help?: string;
  step?: number;
  min?: number;
  max?: number;
  suffix?: string;
  options?: { value: string; label: string }[];
}

const TIMEFRAME_OPTIONS = [
  { value: "5m", label: "tf.5m" },
  { value: "15m", label: "tf.15m" },
  { value: "1h", label: "tf.1h" },
  { value: "4h", label: "tf.4h" },
  { value: "1d", label: "tf.1d" },
];

/** The timeframes a Fibonacci net is measured on. */

/** A layer group's field: its label and help under `field.layers.<group>.<field>`. */
const layer = (group: string, field: string, rest: Omit<FieldMeta, "label" | "help"> = {}): [string, FieldMeta] => [`${group}.${field}`, { label: `field.layers.${group}.${field}.label`, help: `field.layers.${group}.${field}.help`, ...rest }];

export const FIELD_META: Record<string, Record<string, FieldMeta>> = {
  preferences: {
    language: {
      label: "field.preferences.language.label",
      options: [
        { value: "de", label: "field.preferences.language.de" },
        { value: "en", label: "field.preferences.language.en" },
      ],
      help: "field.preferences.language.help",
    },
    theme: {
      label: "field.preferences.theme.label",
      options: [
        { value: "system", label: "field.preferences.theme.system" },
        { value: "light", label: "field.preferences.theme.light" },
        { value: "dark", label: "field.preferences.theme.dark" },
      ],
    },
    setupTimeframe: { label: "field.preferences.setupTimeframe.label", help: "field.preferences.setupTimeframe.help", options: TIMEFRAME_OPTIONS },
    timeZone: { label: "field.preferences.timeZone.label", help: "field.preferences.timeZone.help", options: TIME_ZONES.map((z) => ({ value: z, label: `tz.${z}` })) },
  },
  // one group per layer of the chart's layers menu
  layers: Object.fromEntries([
    layer("events", "bars", { suffix: "field.suffix.bars", step: 10, min: 20, max: 2000 }),
    layer("events", "max", { step: 1, min: 1, max: 2000 }),
    layer("levels", "lookback", { suffix: "field.suffix.bars", step: 50, min: 50, max: 2000 }),
    layer("levels", "perSide", { step: 1, min: 1, max: 6 }),
    layer("levels", "liquidityBars", { suffix: "field.suffix.bars", step: 10, min: 20, max: 2000 }),
    layer("levels", "liquidityTolAtr", { suffix: "field.suffix.atr", step: 0.01, min: 0.02, max: 1 }),
    layer("levels", "liquidityMax", { step: 1, min: 0, max: 10 }),
    layer("fvg", "lookback", { suffix: "field.suffix.bars", step: 10, min: 20, max: 2000 }),
    layer("fvg", "max", { step: 1, min: 1, max: 50 }),
    layer("orderBlocks", "max", { step: 1, min: 1, max: 50 }),
    layer("breakers", "max", { step: 1, min: 1, max: 50 }),
    layer("poi", "max", { step: 1, min: 1, max: 50 }),
    layer("supplyDemand", "lookback", { suffix: "field.suffix.bars", step: 10, min: 20, max: 2000 }),
    layer("supplyDemand", "max", { step: 1, min: 1, max: 50 }),
    layer("srZones", "lookback", { suffix: "field.suffix.bars", step: 50, min: 50, max: 2000 }),
    layer("srZones", "widthAtr", { suffix: "field.suffix.atr", step: 0.1, min: 0.1, max: 5 }),
    layer("srZones", "max", { step: 1, min: 1, max: 12 }),
    layer("dayLevels", "roundStep", { step: 5, min: 0, max: 1000 }),
    layer("candles", "bars", { suffix: "field.suffix.bars", step: 10, min: 20, max: 2000 }),
    layer("candles", "max", { step: 1, min: 1, max: 2000 }),
  ]),
  data: {
    refreshMinutesDaily: { label: "field.data.refreshMinutesDaily.label", suffix: "field.suffix.minutes", step: 1, min: 1 },
    refreshMinutesIntraday: { label: "field.data.refreshMinutesIntraday.label", suffix: "field.suffix.minutes", step: 1, min: 1 },
    newsRefreshMinutes: { label: "field.data.newsRefreshMinutes.label", suffix: "field.suffix.minutes", step: 5, min: 5 },
  },
  platform: {
    registrationOpen: { label: "field.platform.registrationOpen.label", help: "field.platform.registrationOpen.help" },
  },
};

export const ASSET_CLASSES = ["stock", "etf", "crypto", "fx", "metal", "commodity", "index"] as const;
